import { useState, useEffect, useMemo, useCallback } from 'react';
import { Tooltip } from '@mantine/core';
import { ChevronRight, Flag, Users, AlertTriangle, TrendingUp, TrendingDown, Minus, Check, X } from 'lucide-react';
import type {
  AttendanceDrillDownResponse,
  AttendanceDrillDownParticipant,
  AttendanceDrillDownGroup,
  AttendanceParticipantStatus,
  SessionIndicator,
} from '../../../../types';
import { cohortsApi } from '../../../../services/api/cohorts';
import { getAvatarColor } from '../../../../utils/avatarHelpers';
import {
  modalitiesPresent,
  modalityAttendanceLabel,
  modalityAttendancePrefix,
  showsGroupCoaching,
} from '../../../../utils/attendanceModalityLabels';
import { getFlagSeverity, FLAG_SEVERITY } from '../../../../config/severityConfig';
import type { ParticipantFlagCategory } from '../../../../types';
import { useFollowUpSummary } from '../../hooks/useFollowUpSummary';
import type { OpenFollowUpsFn } from '../OverviewTab';
import { useFeatureFlag } from '../../../../stores/featureFlags';
import { DataLibraryDrawer } from '../../../../components/DataLibrary/DataLibraryDrawer';
import type { GroupState } from '../../../../components/common/GroupPanel/GroupPanel';
import type { ActiveFilterRow } from '../../../../components/common/FilterPanel/FilterPanel';
import { getAttendanceCoverageDisplay } from '../../../../utils/attendanceCoverage';
import classes from './AttendanceHealthDrillDown.module.css';

/** MED-661: Map attendance drill-down tab to Data Library group-by field */
const TAB_TO_GROUP: Record<TabKey, GroupState | null> = {
  byParticipant: null,
  bySession: [{ field: 'session_number', order: 'alphabetical' }],
  byGroup: [{ field: 'group', order: 'alphabetical' }],
};

// ============================================================================
// Types
// ============================================================================

type TabKey = 'byParticipant' | 'bySession' | 'byGroup';

interface AttendanceHealthDrillDownProps {
  cohortId: string;
  onAddTask?: (defaults: { title: string; description: string }) => void;
  onTabChange?: () => void;
  onOpenFollowUps?: OpenFollowUpsFn;
}

// ============================================================================
// Constants
// ============================================================================

const TAB_CONFIG: { key: TabKey; label: string }[] = [
  { key: 'byParticipant', label: 'By Participant' },
  { key: 'bySession', label: 'By Session' },
  { key: 'byGroup', label: 'By Group' },
];

const STATUS_LABELS: Record<AttendanceParticipantStatus, string> = {
  action: 'Action',
  monitor: 'Monitor',
  healthy: 'Healthy',
};

const STATUS_CLASSES: Record<AttendanceParticipantStatus, string> = {
  action: classes.statusAction,
  monitor: classes.statusMonitor,
  healthy: classes.statusHealthy,
};

function getAttendanceBarClass(percent: number | null): string {
  if (percent === null) return classes.attendanceBarGreen;
  if (percent >= 80) return classes.attendanceBarGreen;
  if (percent >= 70) return classes.attendanceBarAmber;
  return classes.attendanceBarRed;
}

function formatRelativeDate(isoDate: string | null): string {
  if (!isoDate) return '--';
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
}

// ============================================================================
// Session Indicator Dot
// ============================================================================

function SessionDot({ indicator }: { indicator: SessionIndicator }) {
  const statusClassMap: Record<string, string> = {
    attended: classes.sessionDotAttended,
    present: classes.sessionDotAttended,
    partial: classes.sessionDotPartial,
    absent: classes.sessionDotAbsent,
    excused: classes.sessionDotExcused,
    pending: classes.sessionDotPending,
  };

  const iconMap: Record<string, React.ReactNode> = {
    attended: <Check size={12} />,
    present: <Check size={12} />,
    partial: <span>!</span>,
    absent: <X size={12} />,
    excused: <span>E</span>,
    pending: <span>{indicator.sessionNumber}</span>,
  };

  const status = indicator.status;
  const dotClass = status ? (statusClassMap[status] ?? classes.sessionDotNull) : classes.sessionDotNull;
  const icon = status ? (iconMap[status] ?? <span>{indicator.sessionNumber}</span>) : <span>{indicator.sessionNumber}</span>;

  const countInfo =
    indicator.attendedCount != null && indicator.totalCount != null
      ? ` (${indicator.attendedCount}/${indicator.totalCount} attended)`
      : '';
  const tooltip = `Session ${indicator.sessionNumber}: ${status ?? 'N/A'}${countInfo}`;

  return (
    <Tooltip label={tooltip} withArrow>
      <div className={`${classes.sessionDot} ${dotClass}`}>
        {icon}
      </div>
    </Tooltip>
  );
}

// ============================================================================
// Attendance Bar
// ============================================================================

function AttendanceBar({ percent }: { percent: number | null }) {
  return (
    <div className={classes.attendanceCell}>
      <div className={classes.attendanceBar}>
        <div
          className={`${classes.attendanceBarFill} ${getAttendanceBarClass(percent)}`}
          style={{ width: `${percent ?? 0}%` }}
        />
      </div>
      <span className={classes.attendancePercent}>
        {/* MED-1442: the same empty-rate mark as the Participants tab and coach table. */}
        {getAttendanceCoverageDisplay({ rate: percent, marked: percent === null ? 0 : 1, population: 1 }).rateLabel}
      </span>
    </div>
  );
}

// ============================================================================
// By Participant Tab
// ============================================================================

function ParticipantTable({
  participants,
  onAddTask,
  onViewParticipant,
  userPendingCounts,
  onOpenFollowUps,
}: {
  participants: AttendanceDrillDownParticipant[];
  onAddTask?: (p: AttendanceDrillDownParticipant) => void;
  onViewParticipant?: (participantId: string) => void;
  userPendingCounts?: Record<string, number>;
  onOpenFollowUps?: OpenFollowUpsFn;
}) {
  // MED-1442: the Attendance and Sessions columns are group coaching ("PG" for
  // a peer led cohort). 1:1 and W get a column only when the cohort has them.
  const subType = participants.find(p => p.groupCoachingSubType)?.groupCoachingSubType ?? null;
  const gc = modalityAttendancePrefix('group_coaching', subType);
  const rows = participants.map(p => ({ attendance_by_modality: p.attendanceByModality }));
  const extra = modalitiesPresent(rows).filter(k => k !== 'group_coaching');
  // A 1:1-only cohort has no GC columns.
  const showGc = showsGroupCoaching(rows);
  return (
    // The extra columns can be wider than the card: scroll, do not clip the row actions.
    <div className={classes.tableScroll}>
    <table className={classes.table}>
      <thead className={classes.tableHead}>
        <tr>
          <th>Participant</th>
          {showGc && <th>{gc} attendance</th>}
          {showGc && <th>{gc} sessions</th>}
          {extra.map(k => (
            <th key={k}>{modalityAttendanceLabel(k, subType)}</th>
          ))}
          <th>Status</th>
          <th>Last SGOS Email</th>
          <th></th>
        </tr>
      </thead>
      <tbody className={classes.tableBody}>
        {participants.map(p => {
          const isWithdrawn = p.participantStatus === 'withdrawn';
          return (
          <tr key={p.participantId} style={isWithdrawn ? { opacity: 0.5 } : undefined}>
            <td>
              <div className={classes.participantCell}>
                <div className={classes.avatar} style={{ backgroundColor: getAvatarColor(p.name) }}>
                  {p.initials}
                </div>
                <div>
                  <div className={classes.participantName}>
                    {p.name}
                    {isWithdrawn && (
                      <span style={{
                        display: 'inline-block', backgroundColor: '#E8E8E8', color: '#444444',
                        fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px',
                        padding: '1px 6px', borderRadius: 3, marginLeft: 6,
                      }}>
                        {p.withdrawnAt ? `WITHDRAWN ${new Date(p.withdrawnAt).getMonth() + 1}/${new Date(p.withdrawnAt).getDate()}` : 'WITHDRAWN'}
                      </span>
                    )}
                  </div>
                  {p.groupName && <div className={classes.participantGroup}>{p.groupName}</div>}
                </div>
              </div>
            </td>
            {showGc && (
              <td>
                <AttendanceBar percent={p.attendancePercent} />
              </td>
            )}
            {showGc && (
              <td>
                <div className={classes.sessionIndicators}>
                  {p.sessionIndicators.map(si => (
                    <SessionDot key={si.sessionNumber} indicator={si} />
                  ))}
                </div>
              </td>
            )}
            {extra.map(k => (
              <td key={k}>
                <AttendanceBar percent={p.attendanceByModality?.[k]?.percentage ?? null} />
              </td>
            ))}
            <td>
              <span className={`${classes.statusBadge} ${STATUS_CLASSES[p.status]}`}>
                {STATUS_LABELS[p.status]}
              </span>
            </td>
            <td>
              <span className={classes.sgosDate}>{formatRelativeDate(p.lastSgosEmailAt)}</span>
            </td>
            <td>
              <div className={classes.rowActions}>
                {onAddTask && (
                  <button className={classes.addTaskButton} onClick={() => onAddTask(p)}>
                    Add Task
                  </button>
                )}
                {onOpenFollowUps && !isWithdrawn && userPendingCounts && (userPendingCounts[p.participantId] ?? 0) > 0 && (
                  <button
                    className={classes.reviewFollowUpButton}
                    onClick={() => onOpenFollowUps({ participantId: p.participantId })}
                  >
                    Review Follow Up
                  </button>
                )}
                {onViewParticipant && (
                  <button
                    className={classes.viewAllLink}
                    onClick={() => onViewParticipant(p.participantId)}
                  >
                    view <ChevronRight size={14} />
                  </button>
                )}
              </div>
            </td>
          </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}

// ============================================================================
// By Session Tab
// ============================================================================

function SessionTable({ data }: { data: AttendanceDrillDownResponse }) {
  return (
    <table className={classes.table}>
      <thead className={classes.tableHead}>
        <tr>
          <th>Session</th>
          <th>Attendance</th>
          <th>Engagement</th>
          <th>Attended</th>
          <th>Absent</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody className={classes.tableBody}>
        {data.sessions.map(s => (
          <tr key={s.sessionNumber}>
            <td>
              <div className={classes.sessionTitle}>{s.title}</div>
              {s.date && <div className={classes.sessionDate}>{s.date}</div>}
            </td>
            <td>
              <AttendanceBar percent={s.attendancePercent} />
            </td>
            <td>
              <span style={{ fontWeight: 600 }}>
                {s.engagementAvg !== null ? s.engagementAvg.toFixed(1) : 'N/A'}
              </span>
            </td>
            <td>{s.attendedCount}</td>
            <td>{s.absentCount}</td>
            <td>
              <span
                className={`${classes.statusBadge} ${
                  s.isFlagged ? classes.statusAction : classes.statusHealthy
                }`}
              >
                {s.isFlagged ? 'Flagged' : 'OK'}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ============================================================================
// By Group Tab — Group Row
// ============================================================================

function GroupRow({
  group,
  onAddTask,
}: {
  group: AttendanceDrillDownGroup;
  onAddTask?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const avatarClass =
    group.status === 'action'
      ? classes.groupAvatarAction
      : group.status === 'monitor'
        ? classes.groupAvatarMonitor
        : classes.groupAvatarHealthy;

  const trendClass =
    group.trendDirection === 'up'
      ? classes.trendUp
      : group.trendDirection === 'down'
        ? classes.trendDown
        : classes.trendStable;

  const TrendIcon =
    group.trendDirection === 'up'
      ? TrendingUp
      : group.trendDirection === 'down'
        ? TrendingDown
        : Minus;

  const { detail } = group;

  // Build a summary string from flags
  const flagSummaryParts: string[] = [];
  if (detail.participantsFlagged > 0) {
    flagSummaryParts.push(`${detail.participantsFlagged} participant${detail.participantsFlagged !== 1 ? 's' : ''} flagged`);
  }
  if (detail.surveyFlagCount > 0) {
    flagSummaryParts.push(`${detail.surveyFlagCount} survey red flag${detail.surveyFlagCount !== 1 ? 's' : ''}`);
  }
  if (group.trendDirection === 'down' && group.trendPercent !== null) {
    flagSummaryParts.push(`engagement down ${Math.abs(group.trendPercent)}%`);
  }

  return (
    <div className={`${classes.groupRow} ${group.status !== 'healthy' ? classes.groupRowMonitor : ''}`}>
      <button className={classes.groupRowHeader} onClick={() => setExpanded(prev => !prev)}>
        <div className={classes.groupCell}>
          <div className={`${classes.groupAvatar} ${avatarClass}`}>
            {group.groupInitials}
          </div>
          <div className={classes.groupInfo}>
            <div className={classes.groupName}>{group.groupName}</div>
            <div className={classes.groupMeta}>
              {group.participantCount} participants {group.coachName ? `\u2022 Coach: ${group.coachName}` : ''}
            </div>
          </div>
        </div>
        <AttendanceBar percent={group.attendancePercent} />
        <div style={{ fontWeight: 600, fontSize: 13 }}>
          {group.engagementAvg !== null ? group.engagementAvg.toFixed(1) : 'N/A'}
        </div>
        <div className={classes.sessionIndicators}>
          {group.sessionIndicators.map(si => (
            <SessionDot key={si.sessionNumber} indicator={si} />
          ))}
        </div>
        <span className={`${classes.statusBadge} ${STATUS_CLASSES[group.status]}`}>
          {STATUS_LABELS[group.status]}
        </span>
        <div className={`${classes.trendCell} ${trendClass}`}>
          <TrendIcon size={14} />
          {group.trendPercent !== null ? `${group.trendPercent > 0 ? '+' : ''}${group.trendPercent}%` : 'Stable'}
        </div>
        <span className={classes.viewGroupButton}>View Group</span>
      </button>

      {expanded && (
        <div className={classes.groupDetail}>
          <div className={classes.detailCards}>
            <div className={classes.detailCard}>
              <div className={classes.detailCardLabel}>Participants Flagged</div>
              <div className={classes.detailCardValue}>{detail.participantsFlagged}</div>
              <div className={classes.detailCardSub}>
                {detail.flaggedParticipants?.length > 0 ? (
                  <div className={classes.flaggedAvatars}>
                    {detail.flaggedParticipants.map((fp) => {
                      const initials = fp.name.split(' ').map(w => w.charAt(0).toUpperCase()).slice(0, 2).join('');
                      const tooltipContent = (
                        <div>
                          <div style={{ fontWeight: 600 }}>{fp.name}</div>
                          {fp.flags.map((f, i) => {
                            const severity = getFlagSeverity(f.category as ParticipantFlagCategory);
                            const pillColor = FLAG_SEVERITY[severity].pill.text;
                            return (
                              <div key={i} style={{ fontSize: 11, opacity: 0.85, color: pillColor }}>{f.reason}</div>
                            );
                          })}
                        </div>
                      );
                      return (
                        <Tooltip key={fp.name} label={tooltipContent} openDelay={0} withArrow multiline w={220}>
                          <div
                            className={classes.flaggedAvatar}
                            style={{ backgroundColor: getAvatarColor(fp.name) }}
                          >
                            {initials}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                ) : detail.flaggedNames.length > 0 ? (
                  <div className={classes.flaggedAvatars}>
                    {detail.flaggedNames.map((name) => {
                      const initials = name.split(' ').map(w => w.charAt(0).toUpperCase()).slice(0, 2).join('');
                      return (
                        <Tooltip key={name} label={name} openDelay={0} withArrow>
                          <div
                            className={classes.flaggedAvatar}
                            style={{ backgroundColor: getAvatarColor(name) }}
                          >
                            {initials}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                ) : 'None'}
              </div>
            </div>
            <div className={classes.detailCard}>
              <div className={classes.detailCardLabel}>Avg Attendance</div>
              <div className={`${classes.detailCardValue} ${(detail.avgAttendance ?? 100) < 80 ? classes.detailCardValueRed : ''}`}>
                {detail.avgAttendance !== null ? `${detail.avgAttendance}%` : 'N/A'}
              </div>
              <div className={classes.detailCardSub}>
                {detail.avgAttendance !== null && detail.avgAttendance < 80
                  ? 'Below 80% threshold'
                  : 'On track'}
              </div>
            </div>
            <div className={classes.detailCard}>
              <div className={classes.detailCardLabel}>
                {detail.latestSessionNumber ? `Session ${detail.latestSessionNumber} Engagement` : 'Engagement'}
              </div>
              <div className={classes.detailCardValue}>
                {detail.latestSessionEngagement !== null ? detail.latestSessionEngagement.toFixed(1) : 'N/A'}
              </div>
              <div className={classes.detailCardSub}>
                {detail.latestSessionEngagement !== null && detail.latestSessionEngagement < 3.5
                  ? 'Below average'
                  : 'On track'}
              </div>
            </div>
            <div className={classes.detailCard}>
              <div className={classes.detailCardLabel}>Survey Flags</div>
              <div className={classes.detailCardValue}>{detail.surveyFlagCount}</div>
              <div className={classes.detailCardSub}>
                {detail.surveyFlagNames.length > 0
                  ? detail.surveyFlagNames.map(n => n.split(' ').map(w => w[0] + '.').join(' ')).join(', ')
                  : 'None'}
              </div>
            </div>
          </div>

          {(flagSummaryParts.length > 0 || group.coachNotes) && (
            <div className={classes.coachCallout}>
              <div className={classes.coachCalloutIcon}>
                <AlertTriangle size={16} color="var(--medley-flag-text, #E07B4C)" />
              </div>
              <div className={classes.coachCalloutText}>
                {flagSummaryParts.length > 0 && (
                  <div style={{ fontWeight: 600 }}>{flagSummaryParts.join(', ')}</div>
                )}
                {group.coachNotes && (
                  <div>Coach noted: &ldquo;{group.coachNotes}&rdquo;</div>
                )}
              </div>
              <div className={classes.coachCalloutActions}>
                {onAddTask && (
                  <button className={classes.addTaskButton} onClick={onAddTask}>
                    Add Task
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function AttendanceHealthDrillDown({
  cohortId,
  onAddTask,
  onTabChange,
  onOpenFollowUps,
}: AttendanceHealthDrillDownProps) {
  const attendanceFollowUpsEnabled = useFeatureFlag('attendance_follow_ups');
  const [activeTab, setActiveTab] = useState<TabKey>('byParticipant');
  const [data, setData] = useState<AttendanceDrillDownResponse | null>(null);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [drawerLayoutKey, setDrawerLayoutKey] = useState(0);
  const [drawerGroup, setDrawerGroup] = useState<GroupState | null>(null);
  // MED-764: Participant ID for auto-opening detail modal in Data Library
  const [drawerParticipantId, setDrawerParticipantId] = useState<string | undefined>(undefined);
  // MED-773: Filters for pre-applying group selection in Data Library
  const [drawerFilters, setDrawerFilters] = useState<ActiveFilterRow[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [flaggedOnly, setFlaggedOnly] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null); // null = all

  // E19-6: Fetch follow-up summary for callout banner + per-participant CTA (only when flag enabled)
  const { summary: followUpSummary } = useFollowUpSummary({ cohortId, enabled: attendanceFollowUpsEnabled });

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      try {
        const result = await cohortsApi.getAttendanceDrillDown(cohortId);
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load attendance drill-down:', err);
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [cohortId]);

  // MED-476: Filter participants — "Flagged Only" filters to attendance flags only
  const filteredParticipants = useMemo(() => {
    if (!data) return [];
    let list = data.participants;
    if (flaggedOnly) {
      list = list.filter(p => p.hasAttendanceFlag);
    }
    if (selectedGroup) {
      list = list.filter(p => p.groupId === selectedGroup);
    }
    return list;
  }, [data, flaggedOnly, selectedGroup]);

  // Filter group rows
  const filteredGroups = useMemo(() => {
    if (!data) return [];
    if (selectedGroup) {
      return data.groupRows.filter(g => g.groupId === selectedGroup);
    }
    return data.groupRows;
  }, [data, selectedGroup]);

  // Tab counts — MED-476: use hasAttendanceFlag for attendance-specific filter
  const tabCounts = useMemo(() => ({
    byParticipant: data?.participants.filter(p => flaggedOnly ? p.hasAttendanceFlag : true).length ?? 0,
    bySession: data?.sessions.length ?? 0,
    byGroup: data?.groupRows.length ?? 0,
  }), [data, flaggedOnly]);

  const handleParticipantAddTask = useCallback((p: AttendanceDrillDownParticipant) => {
    if (!onAddTask) return;
    onAddTask({
      title: `${p.name} needs attention`,
      description: `${p.name} has ${p.attendancePercent ?? 0}% ${modalityAttendanceLabel('group_coaching', p.groupCoachingSubType)} and requires follow-up.\n\nGroup: ${p.groupName ?? 'Unassigned'}\nStatus: ${STATUS_LABELS[p.status]}`,
    });
  }, [onAddTask]);

  const handleGroupAddTask = useCallback((group: AttendanceDrillDownGroup) => {
    if (!onAddTask) return;
    const parts: string[] = [`${group.groupName} has ${group.attendancePercent ?? 0}% average attendance.`];
    if (group.detail.participantsFlagged > 0) {
      parts.push(`${group.detail.participantsFlagged} participant(s) flagged: ${group.detail.flaggedNames.join(', ')}`);
    }
    if (group.coachNotes) {
      parts.push(`Coach notes: "${group.coachNotes}"`);
    }
    onAddTask({
      title: `${group.groupName} attendance needs attention`,
      description: parts.join('\n\n'),
    });
  }, [onAddTask]);

  // MED-764: Open Data Library with participant detail modal
  const handleViewParticipant = useCallback((participantId: string) => {
    setDrawerParticipantId(participantId);
    setDrawerGroup(null);
    setDrawerLayoutKey(k => k + 1);
    setDrawerOpened(true);
  }, []);

  if (loading) {
    return (
      <div className={classes.drillDown}>
        <div className={classes.loading}>Loading attendance data...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={classes.drillDown}>
        <div className={classes.emptyState}>Failed to load attendance data</div>
      </div>
    );
  }

  return (
    <>
    <DataLibraryDrawer
      opened={drawerOpened}
      onClose={() => setDrawerOpened(false)}
      defaultCohortId={cohortId}
      defaultTab="attendance"
      defaultGroup={drawerGroup}
      defaultFilters={drawerFilters}
      layoutKey={drawerLayoutKey}
      defaultParticipantId={drawerParticipantId}
    />
    <div className={classes.drillDown}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <span className={classes.headerTitle}>{data.header.title}</span>
          {data.header.warningText && (
            <span className={classes.warningBadge}>{data.header.warningText}</span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className={classes.tabs}>
        {TAB_CONFIG.map(({ key, label }) => {
          const count = tabCounts[key];
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              className={`${classes.tab} ${isActive ? classes.tabActive : ''}`}
              onClick={() => { setActiveTab(key); onTabChange?.(); }}
            >
              {label}
              <span className={classes.tabCount}>{count}</span>
            </button>
          );
        })}
        <button
          className={classes.viewAllLink}
          style={{ marginLeft: 'auto' }}
          onClick={() => {
            setDrawerParticipantId(undefined);
            setDrawerFilters(undefined);
            setDrawerGroup(TAB_TO_GROUP[activeTab]);
            setDrawerLayoutKey(k => k + 1);
            setDrawerOpened(true);
          }}
        >
          View in Data Library <ChevronRight size={14} />
        </button>
      </div>

      {/* Filter Chips */}
      {(activeTab === 'byParticipant' || activeTab === 'byGroup') && (
        <div className={classes.filterChips}>
          {activeTab === 'byParticipant' && (
            <button
              className={`${classes.filterChip} ${flaggedOnly ? classes.filterChipActive : ''}`}
              onClick={() => { setFlaggedOnly(prev => !prev); onTabChange?.(); }}
            >
              <span className={classes.filterChipIcon}><Flag size={12} /></span>
              Attendance Flags Only
            </button>
          )}
          {activeTab === 'byParticipant' && !flaggedOnly && (
            <button
              className={`${classes.filterChip} ${!selectedGroup && !flaggedOnly ? classes.filterChipActive : ''}`}
              onClick={() => { setSelectedGroup(null); onTabChange?.(); }}
            >
              All Participants
            </button>
          )}
          {activeTab === 'byGroup' && (
            <button
              className={`${classes.filterChip} ${!selectedGroup ? classes.filterChipActive : ''}`}
              onClick={() => { setSelectedGroup(null); onTabChange?.(); }}
            >
              All Groups
            </button>
          )}
          {data.groups.map(g => (
            <button
              key={g.id}
              className={`${classes.filterChip} ${selectedGroup === g.id ? classes.filterChipActive : ''}`}
              onClick={() => { setSelectedGroup(prev => prev === g.id ? null : g.id); onTabChange?.(); }}
            >
              <span className={classes.filterChipIcon}><Users size={12} /></span>
              {g.name}
            </button>
          ))}
          {/* MED-773: Secondary "View [Group] in Data Library" link */}
          {selectedGroup && (() => {
            const selectedGroupName = data.groups.find(g => g.id === selectedGroup)?.name;
            return selectedGroupName ? (
              <button
                className={classes.viewAllLink}
                style={{ marginLeft: 'auto' }}
                onClick={() => {
                  setDrawerParticipantId(undefined);
                  setDrawerFilters([{ key: 'group', values: [selectedGroup] }]);
                  setDrawerGroup(TAB_TO_GROUP[activeTab]);
                  setDrawerLayoutKey(k => k + 1);
                  setDrawerOpened(true);
                }}
              >
                View {selectedGroupName} in Data Library <ChevronRight size={14} />
              </button>
            ) : null;
          })()}
        </div>
      )}

      {/* E19-6: Attendance Follow-Up Callout Banner */}
      {followUpSummary && followUpSummary.total_pending > 0 && (
        <div className={classes.followUpCallout}>
          <div className={classes.followUpCalloutContent}>
            <AlertTriangle size={16} className={classes.followUpCalloutIcon} />
            <span className={classes.followUpCalloutText}>
              {followUpSummary.total_pending} Attendance Follow Up{followUpSummary.total_pending !== 1 ? 's' : ''} for Review
            </span>
          </div>
          {onOpenFollowUps && (
            <button className={classes.followUpCalloutButton} onClick={() => onOpenFollowUps()}>
              Review Follow Ups
            </button>
          )}
        </div>
      )}

      {/* By Participant Tab */}
      {activeTab === 'byParticipant' && (
        filteredParticipants.length === 0 ? (
          <div className={classes.emptyState}>
            {flaggedOnly ? 'No participants with attendance flags' : 'No participants found'}
          </div>
        ) : (
          <ParticipantTable
            participants={filteredParticipants}
            onAddTask={handleParticipantAddTask}
            onViewParticipant={handleViewParticipant}
            userPendingCounts={followUpSummary?.user_pending_counts}
            onOpenFollowUps={onOpenFollowUps}
          />
        )
      )}

      {/* By Session Tab */}
      {activeTab === 'bySession' && (
        data.sessions.length === 0 ? (
          <div className={classes.emptyState}>No completed sessions</div>
        ) : (
          <SessionTable data={data} />
        )
      )}

      {/* By Group Tab */}
      {activeTab === 'byGroup' && (
        filteredGroups.length === 0 ? (
          <div className={classes.emptyState}>No groups found</div>
        ) : (
          <div>
            {/* Column headers */}
            <div className={classes.groupTableHeader}>
              <span>Group</span>
              <span>Attendance</span>
              <span>Engagement</span>
              <span>Sessions</span>
              <span>Status</span>
              <span>Trend</span>
              <span></span>
            </div>
            {filteredGroups.map(g => (
              <GroupRow
                key={g.groupId}
                group={g}
                onAddTask={() => handleGroupAddTask(g)}
              />
            ))}
          </div>
        )
      )}
    </div>
    </>
  );
}
